import { cn } from '@/lib/utils';

export interface PercentRingProps {
  /** 0–100. Anything outside is clamped rather than drawn as an over-full arc. */
  value: number;
  /** What the figure is, for a screen reader — "Attendance, 92 per cent". */
  label: string;
  /** The arc's colour, as a `stroke-*` utility or a `text-*` the arc inherits. */
  toneClassName?: string;
  size?: number;
  className?: string;
}

/** The ring's geometry, in the SVG's own units — the box is scaled by `size`. */
const BOX = 36;
const STROKE = 4;
const RADIUS = (BOX - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * One proportion as a small ring with the figure inside it.
 *
 * A table cell's worth of chart: a percentage next to a bar reads as a number
 * with decoration, while a ring reads as "most of it" or "barely any" before
 * the eye reaches the digits — which is what a register is scanned for.
 *
 * SVG rather than a charting library, for the same reason `DonutChart` is: this
 * is two circles, and a dependency for it would be a megabyte (CLAUDE.md §1).
 * The arc grows on a `stroke-dasharray` transition, so it also animates when
 * the number changes under it rather than only on first paint.
 */
export function PercentRing({
  value,
  label,
  toneClassName = 'text-accent',
  size = 40,
  className,
}: PercentRingProps) {
  const safe = Math.max(0, Math.min(100, Math.round(value)));
  const filled = (safe / 100) * CIRCUMFERENCE;

  return (
    <span
      role="img"
      aria-label={`${label}, ${safe} per cent`}
      style={{ width: size, height: size }}
      className={cn('relative inline-grid shrink-0 place-items-center', className)}
    >
      <svg viewBox={`0 0 ${BOX} ${BOX}`} className="absolute inset-0 -rotate-90" aria-hidden>
        <circle
          cx={BOX / 2}
          cy={BOX / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          className="stroke-surface-3"
        />
        <circle
          cx={BOX / 2}
          cy={BOX / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${CIRCUMFERENCE - filled}`}
          className={cn('stroke-current transition-[stroke-dasharray] duration-500', toneClassName)}
        />
      </svg>

      <span className="relative text-2xs font-bold tabular-nums text-ink" aria-hidden>
        {safe}%
      </span>
    </span>
  );
}
